import { gql, useApolloClient } from '@apollo/client';
import {
  BrandKey,
  GetSearchEnabledTagsQuery,
  GetSearchEnabledTagsQueryVariables,
  GetTagQuery,
  GetTagQueryVariables,
  TagType,
} from '../../__generated__/queries-web';
import { getTagTotalArticleUsage } from '../../utils/getTagTotalArticleUsage';
import { useFetch } from '../../utils/useFetch';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SEARCH_TAG_FRAGMENT = gql`
  fragment SearchTag on Tag {
    id
    title
    type
    relationId
    articleCount
    brandedContents {
      isActive
      brandKey
    }
  }
`;

const GET_SEARCH_ENABLED_TAGS = gql`
  query GetSearchEnabledTags(
    $where: TagFilterSearchInput
    $first: UnsignedInt!
    $skip: UnsignedInt!
    $query: NonEmptyTrimmedString
    $like: TagLikeInput
  ) {
    searchTags(
      first: $first
      where: $where
      query: $query
      like: $like
      skip: $skip
    ) {
      id
      title
      type
      relationId
      articleUsages {
        brandKey
        total
      }
      cardCount
      type
      brandedContents {
        isActive
        brandKey
      }
      cards(first: 100) {
        card {
          id
          title
          secondaryTitle
          description
          url
          label
          brandKey
          status
          media
          body
          categories(first: 100) {
            order
            category {
              id
              level
              title
            }
          }
          tags(first: 100) {
            order
            tag {
              id
              title
              type
            }
          }
        }
        tag {
          id
          title
          type
        }
        order
      }
    }
  }
`;

interface SearchTagsEnabledTags {
  opts?: { sort?: boolean };
  search: string;
  skip?: number;
  type?: TagType[];
  brandKey?: BrandKey;
}

export const useSearchEnabledTagsGetter = () => {
  const client = useApolloClient();
  return ({ search, type, skip, brandKey }: SearchTagsEnabledTags) => {
    return client
      .query<GetSearchEnabledTagsQuery, GetSearchEnabledTagsQueryVariables>({
        query: GET_SEARCH_ENABLED_TAGS,
        variables: {
          first: 25,
          skip: skip ?? 0,
          where: {
            ...(brandKey && {
              brandedContents_some: {
                brandKey,
              },
            }),
            title: search,
            ...(type?.length && {
              type_in: type,
            }),
          },
        },
      })
      .then(({ data, errors }) => {
        if (errors?.length) {
          throw errors[0];
        }

        // eslint-disable-next-line fp/no-mutating-methods
        const sortedTags = [...data.searchTags].sort(
          (a, b) =>
            getTagTotalArticleUsage(b.articleUsages) -
            getTagTotalArticleUsage(a.articleUsages),
        );

        return sortedTags;
      });
  };
};

const GET_TAG = gql`
  query GetTag($where: TagUniqueFilterInput!) {
    tag(where: $where) {
      id
      title
    }
  }
`;

export const useTagGetter = () =>
  useFetch<GetTagQuery, GetTagQueryVariables>(GET_TAG);
